import actionHandler from '../../../vendor/lacodix/resourcerer/resources/js/components/actionHandler'

const LONG_PRESS_MS = 500

// Fingerbewegung oberhalb dieser Distanz (px) ist eine Scroll-/Wisch-Geste,
// kein Tap: echte Taps wackeln nur wenige Pixel, beim Hochschieben der Liste
// wandert der Finger deutlich weiter, bevor pointerup auf dem Button landet.
const MOVE_TOLERANCE_PX = 12

/**
 * actionHandler variant with tap/long-press disambiguation.
 *
 * Short tap  -> executes the tap action (endPress payload).
 * Long press -> executes the alternate action (startPress payload), e.g. the
 *               dialog variant of an otherwise instantly-running action.
 *
 * Wire it via pointer events; @click only serves keyboard activation
 * (Enter/Space), pointer ghost clicks are filtered out via event.detail:
 *   @pointerdown="startPress(dialogData)"
 *   @pointerup="endPress(tapData)"
 *   @click="keyClick($event, tapData)"
 *   @pointercancel="cancelPress()" @pointerleave="cancelPress()"
 *   @contextmenu.prevent
 */
export default () => {
  const base = actionHandler()

  return {
    ...base,

    pressing: false,
    _pressTimer: null,
    _longPressFired: false,
    _moved: false,
    _startX: 0,
    _startY: 0,

    // Keep a reference to the base init() so our override still runs it — it
    // registers the pageshow/pagehide listeners that reset the _busy
    // reentrancy guard (bfcache / back-navigation).
    _baseInit: base.init,

    init() {
      this._baseInit()

      // Koordinaten laufen über eigene Listener, weil startPress/endPress aus
      // Blade nur die Action-Payload bekommen (kein $event). Bewegt sich der
      // Finger merklich (Scrollen), wird der Press verworfen — inklusive
      // Long-Press-Timer.
      this._onPointerDown = (event) => {
        this._startX = event.clientX
        this._startY = event.clientY
        this._moved = false
      }
      this._onPointerMove = (event) => {
        if (this.pressing &&
          Math.hypot(event.clientX - this._startX, event.clientY - this._startY) > MOVE_TOLERANCE_PX) {
          this._moved = true
          this.cancelPress()
        }
      }
      this.$el.addEventListener('pointerdown', this._onPointerDown)
      this.$el.addEventListener('pointermove', this._onPointerMove)
    },

    startPress(longPressData = null) {
      this._clearPressTimer()
      this._longPressFired = false
      this.pressing = true

      if (!longPressData) {
        return
      }

      this._pressTimer = setTimeout(() => {
        this._pressTimer = null
        this._longPressFired = true
        this.pressing = false
        this.execute(longPressData)
      }, LONG_PRESS_MS)
    },

    endPress(tapData) {
      const longPressFired = this._longPressFired
      const moved = this._moved
      this.cancelPress()

      if (!longPressFired && !moved) {
        this.execute(tapData)
      }
    },

    /**
     * Keyboard fallback: Enter/Space on a focused button synthesize a click
     * with detail === 0, which the pointer handlers never see. Pointer
     * interactions are fully covered by pointerdown/up - their ghost click
     * carries detail >= 1 and is ignored here, so nothing fires twice.
     */
    keyClick(event, tapData) {
      if (event.detail === 0) {
        this.execute(tapData)
      }
    },

    /**
     * Shared in-flight gate for the event reply buttons.
     *
     * The three buttons of a card (reject/tentative/accept) are independent
     * Alpine components, and the base actionHandler only guards re-entrancy for
     * ~500ms — far shorter than a slow reply that first has to create/freeze the
     * series occurrences server-side. While that single request is in flight the
     * still-visible buttons stay clickable, and a second tap reaches the server
     * after the first reply already committed: the *_series canRun guard
     * (replyValue must be 'none') then fails and Resourcerer aborts with 403.
     *
     * The `eventReplyLock` store (see app.js) is set here on the first tap and
     * released when the triggered Livewire commit settles, blocking every reply
     * button — single event and series alike — for the whole round-trip.
     */
    execute(data, event = null) {
      const lock = window.Alpine?.store?.('eventReplyLock')

      if (lock?.busy) {
        return
      }

      if (lock) {
        lock.busy = true
      }

      // The shared lock is now the single re-entrancy gate for reply buttons.
      // Clear the base handler's own short-lived _busy timer so it can never
      // suppress this already-vetted dispatch — otherwise the lock would be set
      // with no commit firing to release it, freezing every reply button.
      this._busy = false

      base.execute.call(this, data, event)
    },

    cancelPress() {
      this._clearPressTimer()
      this.pressing = false
    },

    _clearPressTimer() {
      if (this._pressTimer) {
        clearTimeout(this._pressTimer)
        this._pressTimer = null
      }
    },

    destroy() {
      this._clearPressTimer()
      this.$el.removeEventListener('pointerdown', this._onPointerDown)
      this.$el.removeEventListener('pointermove', this._onPointerMove)
      base.destroy.call(this)
    }
  }
}
